import { useEffect, useState } from 'react';
import type { OneNightRevealPublic, PrivateView, PublicView, RoleId } from '@ringmaster/shared';
import { ABSTAIN, ROLE_META } from '@ringmaster/shared';
import { useRoom } from '../net/useRoom.js';
import { endVerdict } from '../ui/endVerdict.js';
import { FixedActionBar } from '../ui/FixedActionBar.js';
import { QuietAction } from '../ui/QuietAction.js';
import { Button } from '../ui/Button.js';
import { biggerShowLabel, useBiggerShow } from '../ui/BiggerShowCTA.js';
import { CommunityCTA } from '../ui/CommunityCTA.js';
import { NextShowStrip } from '../ui/NextShowStrip.js';
import type { SittingGame } from '../net/sitting.js';
import { Glyph } from '../ui/Glyph.js';
import { LevelDiamond } from '../ui/LevelDiamond.js';
import { ScoreRevealModal, useScoreReveal, END_CURTAIN_MS } from '../ui/ScoreRevealModal.js';
import { ParticleLayer } from '../fx/particles/ParticleLayer.js';
import { ROLE_GLYPH, ROLE_TONE, roleAccent } from './roleGlyph.js';
import verdictSurvived from '../assets/ceremony/verdict-survived.webp';
import verdictDeceived from '../assets/ceremony/verdict-deceived.webp';
import endVillageHero from '../assets/scenes/bloodmoon/end-village-hero.webp';
import endWolvesHero from '../assets/scenes/bloodmoon/end-wolves-hero.webp';
import whisperBg from '../assets/games/whisper-bg.png';

// Win = gold (both games). Loss = the same Blood Moon crimson on both games —
// only the win path (and the purple hero backdrop/word-reveal) stays arcana
// for The Whisper.
const WIN_ACCENT = 'var(--gold-5, #e6b866)';
const LOSS_ACCENT = 'var(--blood-6, #ff5a4f)';
const WHISPER_LOSS_ACCENT = LOSS_ACCENT;

/**
 * The player's personal verdict card art, chosen by outcome:
 *   devoured  — you died and your side lost (or you simply fell — Blood Moon only)
 *   survived  — you lived and won
 *   deceived  — the wolves won (the village was deceived) — also reused for the
 *               Whisper's "candle died" loss (see THE_WHISPER_SPEC.md — the
 *               spec calls out reusing verdict-deceived/verdict-survived and
 *               skipping verdict-devoured for whisper).
 */
function verdictArtFor(won: boolean, alive: boolean, wolvesWon: boolean, isWhisper: boolean): string {
  if (isWhisper) return won ? verdictSurvived : verdictDeceived;
  if (wolvesWon && won) return verdictDeceived; // a wolf, victorious by deception
  if (!won) return verdictDeceived; // your side was outfoxed
  if (alive) return verdictSurvived; // lived through to victory
  return verdictDeceived; // fallback (devoured art retired from this path)
}

/** Personal verdict headline copy — shared "You won!"/"You lost" across both games. */
function verdictHeadline(won: boolean): string {
  return won ? 'You won!' : 'You lost';
}

/**
 * The Whisper's sub-headline under the personal verdict: word found (VILLAGE-
 * analog win) vs the candle dying. A 2-player table deals no Deceiver at all
 * (see shared/games.ts rolesFor: n<=2 -> 0 deceivers) — in that co-op case a
 * "WOLVES" result still means the candle died, but there's no traitor to blame.
 */
function whisperResultLine(winner: 'VILLAGE' | 'WOLVES', roles: Record<string, RoleId>): string {
  if (winner === 'VILLAGE') return 'The word was found';
  const hadDeceiver = Object.values(roles).includes('DECEIVER');
  return hadDeceiver ? 'The candle died — the Deceiver escapes' : 'The candle died';
}

/**
 * EndScreen — personal win/lose banner + full reveal + match scoreboard.
 *
 * Opens on a suspense CURTAIN: the game can reach END directly from the final
 * lynch (or the night kill), skipping the reveal phases — so the narrator gets
 * a few seconds of drama before the UI spills the outcome.
 *
 * The host gets a Rematch button (same players → back to the lobby, scores
 * kept); everyone else sees "waiting for the host".
 */

export function EndScreen({
  privateView,
  publicView,
  actionOffset = 0,
  sitting = [],
}: {
  privateView: PrivateView;
  publicView: PublicView;
  actionOffset?: number;
  /** Games this room has finished this visit — gates the share strip (issue #55). */
  sitting?: SittingGame[];
}) {
  const { send } = useRoom();
  const result = publicView.result;
  // One Night is decided by the card you HOLD at dawn, not the one you were
  // dealt — see ui/endVerdict.ts. Every other game: dealt card, unchanged.
  const { role: myRole, dealtRole, won } = endVerdict(publicView, privateView);
  const meta = ROLE_META[myRole];
  const isHost = publicView.hostId === privateView.playerId;
  const isWhisper = publicView.gameId === 'whisper';
  const isOneNight = publicView.gameId === 'onenight';

  // One Night skips the curtain entirely: it has just spent a full
  // ONENIGHT_RESOLVE phase (10s) resting on the tally with the verdict narration
  // playing, so a second suspense beat here replays a drama the table already
  // had — and in a ~10-minute game that reads as the app being slow. The curtain
  // exists for Blood Moon, which can reach END directly from a lynch or a night
  // kill with no reveal phase at all, so its narrator would otherwise be talking
  // over an already-spoiled screen.
  const [curtain, setCurtain] = useState(!isOneNight);
  useEffect(() => {
    if (!curtain) return;
    const t = setTimeout(() => setCurtain(false), END_CURTAIN_MS);
    return () => clearTimeout(t);
  }, [curtain]);

  // The score reveal opens a few seconds AFTER the curtain lifts — `!curtain`
  // arms the hook's open delay, so the player gets to read the ending (who fell,
  // the full role reveal, the night's truth) before the ceremony covers it.
  // Nothing to show for a spectator (no privateView reaches this screen) or
  // before END (`awards` is null then).
  const score = useScoreReveal(publicView, privateView, !curtain);

  // The later half of the "five is enough" offer: a table that has grown into a
  // bigger game hears about it once more, beside the rematch. Armed only once
  // the curtain has lifted, so the beacon means the host actually saw it.
  const biggerShow = useBiggerShow({
    code: publicView.code,
    gameId: publicView.gameId ?? 'bloodmoon',
    playerCount: publicView.players.length,
    isHost,
    surface: 'end',
    enabled: !curtain,
  });

  if (curtain) {
    return (
      <div
        style={{
          minHeight: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          background: isWhisper
            ? 'linear-gradient(180deg, #000000 0%, #160b26 100%)'
            : 'linear-gradient(180deg, #000000 0%, #0d0507 100%)',
          gap: 'var(--space-5)',
          padding: 'var(--space-5)',
        }}
      >
        <div style={{ animation: 'end-curtain-pulse 2.2s ease-in-out infinite' }}>
          <Glyph name={isWhisper ? 'candle' : 'moonFull'} size={52} tone={isWhisper ? 'arcana' : 'blood'} />
        </div>
        <p
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 24,
            color: 'rgba(255,255,255,0.85)',
            margin: 0,
            textAlign: 'center',
            letterSpacing: '0.04em',
          }}
        >
          {isWhisper ? 'The candle gutters…' : 'The tale reaches its end…'}
        </p>
        <p style={{ color: 'var(--text-tertiary)', fontSize: 14, margin: 0, textAlign: 'center' }}>
          Listen to the Ringmaster.
        </p>
        <style>{`
          @keyframes end-curtain-pulse {
            0%, 100% { opacity: 0.45; transform: scale(1); }
            50%      { opacity: 1; transform: scale(1.06); }
          }
          @media (prefers-reduced-motion: reduce) {
            @keyframes end-curtain-pulse {
              0%, 100% { opacity: 0.8; transform: none; }
            }
          }
        `}</style>
      </div>
    );
  }

  const wolvesWon = result?.winner === 'WOLVES';
  const verdictArt = verdictArtFor(won, privateView.alive, wolvesWon, isWhisper);
  const heroArt = wolvesWon ? endWolvesHero : endVillageHero;
  const lossAccent = isWhisper ? WHISPER_LOSS_ACCENT : LOSS_ACCENT;

  return (
    <div
      style={{
        position: 'relative',
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        background: isWhisper
          ? won
            ? 'linear-gradient(180deg, #000000 0%, #140c06 100%)'
            : 'linear-gradient(180deg, #000000 0%, #160b26 100%)'
          : won
            ? 'linear-gradient(180deg, #000000 0%, #140c06 100%)'
            : 'linear-gradient(180deg, #000000 0%, #1a0000 100%)',
        color: 'var(--text-primary)',
        padding: 'var(--space-5)',
        paddingTop: 'max(var(--space-8), env(safe-area-inset-top, 0px) + var(--space-6))',
        // Room for the fixed rematch bar on the host device.
        // Reserve the fixed bar's height so the ledger never hides under it.
        // Shrunk with the row it measures (issue #29): "Back to lobby" is now a
        // 40px quiet control rather than a 52px pill, and the upsell — when
        // offered — is the only pill that can wrap on a narrow phone. Same
        // idiom as the lobby's `fit` reserve.
        paddingBottom: isHost ? 98 + (biggerShow ? 72 : 0) + actionOffset : 'var(--space-5)',
        gap: 'var(--space-5)',
        overflow: 'hidden',
      }}
      className="game-screen"
    >
      {score.open && (
        <ScoreRevealModal
          awards={score.awards}
          won={won}
          onDismiss={score.dismiss}
          startPoints={score.startPoints}
          surface="phone"
        />
      )}

      {/* Winner hero backdrop (portrait crop), dimmed under the content.
          Whisper swaps the Blood Moon village/wolves hero art for the séance
          backdrop over an arcana wash — never the crimson bloodmoon heroes. */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 0,
          pointerEvents: 'none',
          backgroundImage: isWhisper
            ? `linear-gradient(180deg, rgba(22,11,38,0.78) 0%, rgba(22,11,38,0.55) 45%, var(--bg-base) 100%), url(${whisperBg})`
            : `linear-gradient(180deg, rgba(6,4,6,0.72) 0%, rgba(6,4,6,0.5) 45%, var(--bg-base) 100%), url(${heroArt})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
        }}
      />

      {/* Win / lose banner — a personal verdict card over the hero. */}
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          textAlign: 'center',
          overflow: 'hidden',
          background: won ? 'rgba(230,184,102,0.1)' : isWhisper ? 'rgba(91,63,168,0.18)' : 'rgba(153,0,0,0.15)',
          border: `1px solid ${won ? 'var(--gold-3, #a9762f)' : 'var(--blood-3, #802636)'}`,
          borderRadius: 'var(--radius-card)',
          padding: 'var(--space-6)',
          animation: 'end-appear 0.5s var(--ease-standard)',
        }}
      >
        {/* The player's verdict card art (survived / deceived — devoured retired
            for whisper, see THE_WHISPER_SPEC.md). */}
        <img
          src={verdictArt}
          alt=""
          aria-hidden
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.28 }}
        />
        {/* A win rains gold dust / laurel leaves; a loss lets ash fall. Phone-capped. */}
        <ParticleLayer preset={won ? 'goldDust' : 'ash'} intensity={won ? 0.8 : 0.6} slot={1} />
        <div style={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'center', marginBottom: 'var(--space-3)' }}>
          <Glyph name={won ? 'laurel' : isWhisper ? 'candle' : 'skull'} size={56} tone={won ? 'gold' : 'blood'} />
        </div>
        <h1
          style={{
            position: 'relative',
            zIndex: 1,
            fontFamily: 'var(--font-display)',
            fontSize: 32,
            margin: '0 0 var(--space-2)',
            color: won ? WIN_ACCENT : lossAccent,
          }}
        >
          {verdictHeadline(won)}
        </h1>
        <div style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-3)' }}>
          <Glyph name={ROLE_GLYPH[myRole]} size={22} tone={ROLE_TONE[myRole]} />
          <span style={{ color: 'var(--text-secondary)', fontSize: 16 }}>{meta.label}</span>
        </div>
        {/* Your card moved in the night. Say so plainly here, next to the
            verdict it decided — the truth panel's full list is further down. */}
        {dealtRole && (
          <p style={{ position: 'relative', zIndex: 1, color: 'var(--text-tertiary)', fontSize: 13, margin: '0 0 var(--space-2)' }}>
            You were dealt {ROLE_META[dealtRole].label} — your card was swapped in the night.
          </p>
        )}
        {result && (
          <p style={{ position: 'relative', zIndex: 1, color: 'var(--text-tertiary)', fontSize: 14, margin: 0 }}>
            {isWhisper
              ? whisperResultLine(result.winner, result.roles)
              : `${result.winner === 'VILLAGE' ? 'The Village' : 'The Wolves'} win!${result.atDawn ? ' (at dawn)' : ''}`}
          </p>
        )}
        {/* Whisper only: reveal the secret word everyone was hunting for/hiding. */}
        {isWhisper && publicView.whisper?.word && (
          <p
            style={{
              position: 'relative',
              zIndex: 1,
              fontFamily: 'var(--font-display)',
              fontSize: 20,
              letterSpacing: '0.06em',
              margin: 'var(--space-3) 0 0',
              color: 'var(--arcana-4, #c9b6ff)',
              textShadow: '0 0 24px rgba(139,92,246,0.6)',
            }}
          >
            The word was — {publicView.whisper.word.toUpperCase()}
          </p>
        )}
      </div>

      {/* Full reveal + match scoreboard — the Ringmaster's final ledger.
          Whisper shows cumulative session points prominently (score-sorted,
          same as bloodmoon) since PlayerPublic.score carries the running total. */}
      {result && (
        <div
          style={{
            position: 'relative',
            zIndex: 1,
            background: 'linear-gradient(180deg, rgba(20,12,7,0.82) 0%, rgba(11,7,5,0.9) 100%)',
            border: '1px solid var(--gold-2, #7a4c1e)',
            borderRadius: 'var(--radius-card)',
            padding: 'var(--space-5)',
            flex: 1,
            boxShadow: 'var(--shadow-card)',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              color: 'var(--gold-5, #e6b866)',
              fontSize: 12,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              marginBottom: 'var(--space-4)',
            }}
          >
            <span>Full role reveal</span>
            <span>{isWhisper ? 'Total points' : 'Score'}</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            {[...publicView.players]
              .sort((a, b) => b.score - a.score)
              .map((p) => {
                const role = result.roles[p.id] ?? p.revealedRole;
                const roleMeta = role ? ROLE_META[role] : null;
                const isWolf = !isWhisper && role === 'WEREWOLF';
                const isDeceiver = isWhisper && role === 'DECEIVER';
                return (
                  <div
                    key={p.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '10px var(--space-3)',
                      borderRadius: 12,
                      background: isWolf ? 'rgba(153,0,0,0.1)' : isDeceiver ? 'rgba(91,63,168,0.16)' : 'rgba(255,255,255,0.03)',
                      border: isWolf
                        ? '1px solid rgba(153,0,0,0.3)'
                        : isDeceiver
                          ? '1px solid rgba(139,92,246,0.35)'
                          : '1px solid transparent',
                    }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', minWidth: 0 }}>
                      <span style={{ color: p.alive ? 'var(--verdigris-3, #3d7a6c)' : 'var(--text-tertiary)', fontSize: 12 }}>
                        {p.alive ? '●' : '○'}
                      </span>
                      {/* The lifetime badge, same component as the medallion pin
                          and the ceremony's bar caps — and the same one the
                          desktop ending shows, so the two surfaces match.
                          Renders nothing for a seat with no finished game (see
                          LevelDiamond), so no guard here. */}
                      <LevelDiamond level={p.level} size={20} className="end-lvl" />
                      <span
                        style={{
                          fontSize: 15,
                          color: p.alive ? 'var(--text-primary)' : 'var(--text-secondary)',
                          textDecoration: p.alive ? 'none' : 'line-through',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {p.name}
                      </span>
                      {role && roleMeta && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, color: roleAccent(role as RoleId), flexShrink: 0 }}>
                          <Glyph name={ROLE_GLYPH[role as RoleId]} size={14} tone={ROLE_TONE[role as RoleId]} />
                          <span>{roleMeta.label}</span>
                        </span>
                      )}
                    </span>
                    <span
                      style={{
                        flexShrink: 0,
                        fontSize: isWhisper ? 16 : 13,
                        fontWeight: 700,
                        color: p.score > 0 ? 'var(--gold-6, #f7e3b0)' : 'var(--text-tertiary)',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {p.score} pt{p.score === 1 ? '' : 's'}
                    </span>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* One Night's own addendum — centre cards + who was dealt vs. who they
          ended as (only for players a swap actually touched) + who died. The
          shared reveal above already covers final roles/scores; this fills
          in the swap-specific truth (see OneNightRevealPublic). */}
      {isOneNight && publicView.oneNight?.reveal && (
        <OneNightTruthPanel
          reveal={publicView.oneNight.reveal}
          lynchResult={publicView.lynchResult}
          nameMap={Object.fromEntries(publicView.players.map((p) => [p.id, p.name]))}
        />
      )}

      {/* Rematch (host) / waiting note (everyone else) */}
      {isHost ? (
        <FixedActionBar offsetBottom={actionOffset}>
          {/* One primary, one secondary, one quiet (issue #29, decided
              2026-08-30). The two pills share a container; "Back to lobby" sits
              BELOW them in its own — which is both the visual demotion and what
              keeps the adjacent-button cluster at two (R-001). */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--space-2)' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', width: '100%' }}>
              {/* Rematch restarts the SAME game with no lobby stop: PLAY_AGAIN
                  resets the room to LOBBY (roster + gameId kept) and START_GAME
                  deals immediately. Same socket = in-order delivery, and the
                  server handles both synchronously. If the roster shrank below
                  minPlayers while the game ran, START is a silent engine no-op
                  (Blood Moon and One Night narrate NEED_PLAYERS; Whisper rejects
                  it silently) and the table simply sits in
                  the lobby — exactly today's behaviour, now explained. */}
              <Button
                variant="primary"
                tone="gold"
                // Keyboard/gamepad affordance — Enter fires the rematch. Focus
                // treatment unchanged.
                autoFocus
                onClick={() => {
                  send({ type: 'PLAY_AGAIN' });
                  send({ type: 'START_GAME' });
                }}
              >
                Play again
              </Button>
              {/* One tap into the bigger show the table just became big enough
                  for — the same host-gated SWITCH_GAME the picker sends, which
                  re-inits the room into that game's LOBBY (seats kept, curtain
                  still the host's to raise). See ui/BiggerShowCTA.tsx. */}
              {biggerShow && (
                <Button variant="secondary" onClick={biggerShow.act}>
                  {biggerShowLabel(biggerShow.fit, publicView.players.length)}
                </Button>
              )}
            </div>
            {/* Back to the LOBBY, not into the picker — PLAY_AGAIN alone resets
                the room to LOBBY keeping roster + gameId, and "Change game" is
                a pill away from there. Kept in step with the desktop end screen
                (discord/desktop/screens/EndContent.tsx). */}
            <QuietAction onClick={() => send({ type: 'PLAY_AGAIN' })}>Back to lobby</QuietAction>
          </div>
        </FixedActionBar>
      ) : (
        <div
          style={{
            position: 'relative',
            zIndex: 1,
            background: 'rgba(255,255,255,0.04)',
            borderRadius: 16,
            padding: 'var(--space-4)',
            textAlign: 'center',
          }}
        >
          <p style={{ color: 'var(--text-tertiary)', fontSize: 13, margin: 0, animation: 'hand-pulse 2.5s ease-in-out infinite' }}>
            Waiting for the host to start a rematch…
          </p>
        </div>
      )}

      {/* A hot sitting's take-away — host only, and only from the second
          finished game of the visit (issue #55). Deliberately in the content
          flow, like the community link below it: the fixed bar already holds
          the screen's one primary (R-001 / issue #29). */}
      <NextShowStrip
        isHost={isHost}
        sitting={sitting}
        roster={publicView.players}
        surface="phone"
        style={{ position: 'relative', zIndex: 1 }}
      />

      {/* The scout's exit. END is where a table breaks up and where anyone who
          wandered in alone finds out there is somewhere to find a next table.
          Deliberately in the content flow and deliberately an anchor, NOT a
          fourth action beside Play again / the upsell / Back to lobby — that
          row is already the crowded one the owner called out (issue #29, rule
          R-001). Every seat sees it, host included. */}
      <CommunityCTA surface="end" style={{ position: 'relative', zIndex: 1 }} />

      <style>{`
        @keyframes end-appear {
          from { opacity: 0; transform: scale(0.95); }
          to   { opacity: 1; transform: scale(1); }
        }
        @keyframes hand-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        /* The badge is a fixed-size flex item beside a name that may be very long
           — without this it shrinks with the name instead of the name eliding. */
        .end-lvl { flex: 0 0 auto; }
      `}</style>
    </div>
  );
}

/**
 * OneNightTruthPanel — the One-Night-specific slice of the ending: the three
 * face-down centre cards nobody was dealt, plus every player whose held card
 * at dawn (`finalRoles`) DIFFERS from what they were dealt (`startRoles`) —
 * that's the swap's whole point, so only the changed rows are worth calling
 * out (the shared scoreboard above already lists final roles for everyone) —
 * plus the per-voter tally (who everyone pointed at) and the honest verdict:
 * `killedIds` can name SEVERAL players (a tie kills the whole tied group) or
 * NONE at all (the scattered-room rule — nobody drew more than one vote), so
 * this never assumes a single `lynchResult.targetId`.
 */
function OneNightTruthPanel({
  reveal,
  lynchResult,
  nameMap,
}: {
  reveal: OneNightRevealPublic;
  lynchResult: PublicView['lynchResult'];
  nameMap: Record<string, string>;
}) {
  const changed = Object.entries(reveal.startRoles).filter(([id, start]) => reveal.finalRoles[id] !== start);
  const killedSet = new Set(reveal.killedIds);
  return (
    <div
      style={{
        position: 'relative',
        zIndex: 1,
        background: 'linear-gradient(180deg, rgba(20,12,7,0.82) 0%, rgba(11,7,5,0.9) 100%)',
        border: '1px solid var(--gold-2, #7a4c1e)',
        borderRadius: 'var(--radius-card)',
        padding: 'var(--space-5)',
        boxShadow: 'var(--shadow-card)',
      }}
    >
      <div style={{ color: 'var(--gold-5, #e6b866)', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 'var(--space-4)' }}>
        The night&rsquo;s truth
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', marginBottom: changed.length > 0 ? 'var(--space-4)' : 0 }}>
        <span style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>
          {reveal.wolvesInPlay ? 'Face-down in the centre' : 'Face-down in the centre — both wolves sat out'}
        </span>
        <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
          {reveal.centre.map((role, i) => (
            <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 14, color: roleAccent(role) }}>
              <Glyph name={ROLE_GLYPH[role]} size={16} tone={ROLE_TONE[role]} />
              {ROLE_META[role].label}
            </span>
          ))}
        </div>
      </div>

      {changed.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          <span style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>Cards that moved</span>
          {changed.map(([id, start]) => {
            const final = reveal.finalRoles[id]!;
            return (
              <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 14, color: 'var(--text-primary)' }}>
                <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nameMap[id] ?? id}</span>
                <span style={{ color: 'var(--text-tertiary)' }}>was dealt</span>
                <span style={{ color: roleAccent(start) }}>{ROLE_META[start].label}</span>
                <span style={{ color: 'var(--text-tertiary)' }}>→ ended as</span>
                <span style={{ color: roleAccent(final) }}>{ROLE_META[final].label}</span>
              </div>
            );
          })}
        </div>
      )}

      {lynchResult && (
        <div style={{ marginTop: 'var(--space-4)' }}>
          <span style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>How everyone voted</span>
          <div style={{ display: 'flex', flexDirection: 'column', marginTop: 'var(--space-2)' }}>
            {Object.entries(lynchResult.votes).map(([voterId, targetId]) => {
              const isAbstain = targetId === ABSTAIN;
              const targetName = isAbstain ? 'Abstain' : (nameMap[targetId] ?? targetId);
              const isCondemned = !isAbstain && killedSet.has(targetId);
              return (
                <div
                  key={voterId}
                  style={{
                    display: 'flex', justifyContent: 'space-between', fontSize: 14,
                    color: 'rgba(255,255,255,0.82)', padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.07)',
                  }}
                >
                  <span>{nameMap[voterId] ?? voterId}</span>
                  <span
                    style={{
                      fontVariantNumeric: 'tabular-nums',
                      color: isAbstain
                        ? 'var(--text-tertiary)'
                        : isCondemned
                          ? 'var(--blood-6, #ff5a4f)'
                          : 'rgba(255,255,255,0.6)',
                    }}
                  >
                    {isAbstain ? '— abstain' : `→ ${targetName}`}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div
        style={{
          marginTop: 'var(--space-4)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 14,
          color: reveal.killedIds.length > 0 ? 'var(--blood-6, #ff5a4f)' : 'var(--text-tertiary)',
        }}
      >
        <Glyph name={reveal.killedIds.length > 0 ? 'noose' : 'scales'} size={16} tone={reveal.killedIds.length > 0 ? 'blood' : 'bone'} />
        <span>
          {reveal.killedIds.length > 0
            ? `The vote took ${reveal.killedIds.map((id) => nameMap[id] ?? id).join(', ')}`
            : 'The vote scattered — nobody drew enough votes to die'}
        </span>
      </div>
    </div>
  );
}
